import {
  SubItem,
  SubItems,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import {
  ActivePromotion,
  CartProductResponse,
  IDeferredPromotion,
  IGroup,
  Item,
} from '../graphQL/commons'
import { ICatalogItem, ICustomFields, IProduct, IRoundingRules, TBundle } from './catalog'
import { PrefillCart } from './prefillCart'
import { ICommonActivePromotionFields } from './promotions'
import { IConfig } from './config'
import { Referrers } from '../consts/defaultConfigValues/defaultConstants'
import { RootState } from '../redux/store'
import { SessionDataArgs } from '../graphQL/queries/getSession/types'

export enum FeeType {
  SERVICE = 'SERVICE',
  MATERIAL = 'MATERIAL',
  PERCEPTION = 'PERCEPTION',
}
export interface FeeThreshold {
  type?: FeeType
  threshold?: number
  fee?: number
}

export interface ActiveFee {
  fee?: FeeThreshold
  total?: number
}
export interface ICartProduct extends Omit<Item, 'jumpQty' | 'layerDown' | 'layerQty' | 'layerUp' | 'palletDown' | 'palletQty' | 'palletUp'> {
  step?: number
  id: string
  roundingRules?: IRoundingRules[]
}

export interface ISummaryItem {
  name: string
  value: number
}

export interface ISummaryCustomFields {
  summary?: ISummaryItem[]
}

export interface ICartGroup extends Omit<IGroup, 'items'> {
  items: ICartProduct[]
}
export interface ICartProductInput extends Pick<Item, 'sku' | 'quantity'> {
  selectedPackage?: string
}

export interface CartQuantities extends Record<string, number> {}

export interface IPlaceOrderFlow {
  actions: string[]
  isDone: boolean
  pushAfter?: string
}

export interface ICart {
  id: string | null
  items: ICartProduct[]
  groups?: ICartGroup[]
  itemsTotal: number
  total: number
  subtotal: number
  status: string | null
  warnings: string[]
  alerts: string[]
  customFields?: ICustomFields | ISummaryCustomFields | null
  createdAt: string | null
  updatedAt: string | null
  disablePlaceOrder: boolean
  recommendedProducts: {
    show: boolean
    closeButton: string
    productsSkus: string[]
  }
  quantitiesMap: CartQuantities
  activePromotions: ActivePromotion[]
  activeFees: ActiveFee[]
  totalFee: number
  prefillCart: PrefillCart
  linkedProducts: {
    show: boolean
    items: IProduct[]
    continueButton: string
  }
  placeOrderFlow: IPlaceOrderFlow
  deferredPromotions: IDeferredPromotion[] | null
  freeGoodsList?: ICartProduct[]
}

export interface CartTotals {
  totalAmountWithoutDiscount: number
  total: number
}

export interface ITransformCartProduct {
  activePromotionFields: ICommonActivePromotionFields
  cartProduct: ICartProduct
  activePromotion: ActivePromotion
  activePromotions: ActivePromotion[]
  config?: IConfig
  cartProducts?: ICartProduct[]
}

export type TransformAction<T, R> = (args: T) => R

export type Condition<T> = (args: T) => boolean

export interface IConditionAction<T, R> {
  condition: Condition<T>
  transformAction: TransformAction<T, R>
}

export type ConditionMatchPromotions = (
  cartProduct: ICartProduct,
  activePromotion?: ActivePromotion,
) => boolean

export type ConditionMatchParentProduct = (
  productParent: ICartProduct,
  activePromotion?: ActivePromotion,
) => boolean

export type ConditionNotRemoveProduct = (
  childrenProduct: ICartProduct,
) => boolean

export interface IParentCartProductMap {
  sku: string
  itemExtra: ICartProduct
}

export interface ITransformCartProductSubitems {
  cartProduct: ICartProduct
  activePromotion?: ActivePromotion
}

export interface ITransformCartProductsParams {
  cartProducts: ICartProduct[]
  activePromotions: ActivePromotion[]
  config?: IConfig
}

export interface IValidateActivePromotionParams {
  cartProducts: ICartProduct[]
  activePromotion: ActivePromotion
}

export interface IAddCartProductArgs {
  product: IProduct
  value: number
  newValue: number
  referrer?: Referrers
  isBundle?: boolean
  newSubItems?: SubItem | TBundle
  currentSubItems?: SubItems
  currentRoute?: string
  resultFixed?: boolean
  isPackage?: boolean
  isFromCounterButtons?: boolean
}

export interface ICartResponseArgs {
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>
  state: RootState
  data: CartProductResponse
  categories?: ICatalogItem[]
  sessionData?: SessionDataArgs
}
